import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { auditTime, catchError, distinctUntilChanged, filter, fromEvent, map, merge, Observable, of, shareReplay, Subject, Subscription, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { isTemporaryRequestFailure, quietRead, QUIET_HTTP_ERRORS } from '../common/background-request';

/**
 * One What's On post as the paid business-detail placement publishes it
 * (ERP `api/whats-on/branches/{branchId}/posts`). Only the public allowlist reaches here.
 */
export interface WhatsOnPost {
  id: number;
  title: string;
  badge: string;
  availabilityText: string;
  lead: string;
  /** The body split into paragraphs. */
  body: string[];
  sortOrder: number;
  image: WhatsOnPostImage | null;
}

export interface WhatsOnPostImage {
  altText: string;
  /** 2:3 crop. Media is gated per request, so these are API URLs, never cached copies. */
  mobile: string;
  tablet: string | null;
}

export interface WhatsOnPostsPage {
  branchId: number;
  total: number;
  posts: WhatsOnPost[];
}

/** List cards ask one at a time per card; keep them from crowding out card photos. */
const MAX_PARALLEL_AVAILABILITY_READS = 4;
/** Enough for several list batches (the list itself caches 160 businesses). */
const AVAILABILITY_CACHE_LIMIT = 320;
export const WHATS_ON_PAGE_SIZE = 10;

/**
 * Reads the paid What's On placement. The API is the gate: a branch is available only
 * with paid coverage and at least one live post, so the frontend never decides that.
 * `environment.whatsOnPlacementEnabled` only stops requests to an API that is not deployed.
 */
@Injectable({ providedIn: 'root' })
export class WhatsOnService {
  private readonly context = new HttpContext().set(QUIET_HTTP_ERRORS, true);
  private readonly availabilityCache = new Map<number, Observable<boolean>>();
  private readonly refreshRequested = new Subject<void>();
  // Share answers for the current view without expiring or polling while the visitor reads.
  // A new group of mounted controls starts fresh; removed controls leave no listeners.
  // Focus/visibility events also cover returning from the separate Marketplace window.
  private readonly refreshes = merge(
    of(undefined),
    merge(this.refreshRequested, fromEvent(window, 'focus'), fromEvent(document, 'visibilitychange')).pipe(auditTime(0))
  ).pipe(
    filter(() => document.visibilityState !== 'hidden'),
    tap(() => this.availabilityCache.clear()),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  private readonly waiting: Array<() => void> = [];
  private running = 0;

  constructor(private http: HttpClient) {}

  get enabled(): boolean {
    return environment.whatsOnPlacementEnabled === true;
  }

  /**
   * Whether this branch shows What's On. Shared and cached per branch, so every card and
   * the detail page reuse one request until a view/return event or explicit refresh.
   * Pending reads are shared too. Temporary failures are forgotten so the next read can retry.
   */
  availability(branchId: number): Observable<boolean> {
    if (!this.enabled || !isBranchId(branchId)) return of(false);
    const cached = this.availabilityCache.get(branchId);
    if (cached) return cached;

    // The timeout includes time queued behind other cards, hence longer than the default.
    const read: Observable<boolean> = quietRead(() => this.limited(() => this.http.get<unknown>(
      this.apiUrl(`api/whats-on/branches/${branchId}/availability`), { context: this.context }
    )), 30000).pipe(
      map(response => field<boolean>(response, 'whatsOnAvailable') === true),
      catchError(error => {
        if (isTemporaryRequestFailure(error) && this.availabilityCache.get(branchId) === read) {
          this.availabilityCache.delete(branchId);
        }
        return of(false);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.availabilityCache.set(branchId, read);
    if (this.availabilityCache.size > AVAILABILITY_CACHE_LIMIT) {
      this.availabilityCache.delete(this.availabilityCache.keys().next().value);
    }
    return read;
  }

  /** Mounted UI follows changes; callers needing just one bounded cached answer use availability. */
  watchAvailability(branchId: number): Observable<boolean> {
    if (!this.enabled || !isBranchId(branchId)) return of(false);
    return this.refreshes.pipe(
      switchMap(() => this.availability(branchId)),
      distinctUntilChanged()
    );
  }

  /** Discard completed and pending cached answers without allowing late replies to restore them. */
  refreshAvailability(): void {
    this.availabilityCache.clear();
    this.refreshRequested.next();
  }

  /** One page of posts, in the restaurant's own order. Not cached: posts change. */
  posts(branchId: number, offset = 0, limit = WHATS_ON_PAGE_SIZE): Observable<WhatsOnPostsPage> {
    if (!this.enabled || !isBranchId(branchId)) {
      return throwError(() => new Error('What\'s On is not available for this business.'));
    }
    const params = new HttpParams()
      .set('offset', Math.max(0, Math.floor(offset)))
      .set('limit', Math.min(30, Math.max(1, Math.floor(limit))));
    return quietRead(() => this.http.get<unknown>(
      this.apiUrl(`api/whats-on/branches/${branchId}/posts`), { params, context: this.context }
    )).pipe(map(response => this.toPage(branchId, response)));
  }

  private toPage(branchId: number, response: unknown): WhatsOnPostsPage {
    const rows = field<unknown[]>(response, 'posts');
    const posts = (Array.isArray(rows) ? rows : [])
      .map(row => this.toPost(row))
      .filter((post): post is WhatsOnPost => !!post)
      .map((post, index) => ({ post, index }))
      .sort((a, b) => (a.post.sortOrder - b.post.sortOrder) || (a.index - b.index))
      .map(entry => entry.post);
    const total = Number(field<number>(response, 'total'));
    return { branchId, total: Number.isFinite(total) && total >= posts.length ? total : posts.length, posts };
  }

  private toPost(row: unknown): WhatsOnPost | null {
    const id = Number(field<number>(row, 'id'));
    const title = text(field(row, 'title'));
    if (!Number.isInteger(id) || id <= 0 || !title) return null;
    const media = field<unknown>(row, 'media');
    const mobile = this.mediaUrl(field(media, 'mobileUrl'));
    return {
      id,
      title,
      badge: text(field(row, 'badge')),
      availabilityText: text(field(row, 'availabilityText')),
      lead: text(field(row, 'lead')),
      body: text(field(row, 'body')).split(/\r?\n/).map(line => line.trim()).filter(Boolean),
      sortOrder: Number(field<number>(row, 'sortOrder')) || 0,
      image: mobile ? {
        altText: text(field(media, 'altText')),
        mobile,
        tablet: this.mediaUrl(field(media, 'tabletUrl'))
      } : null
    };
  }

  /**
   * Media URLs arrive as logical API paths (`/api/whats-on/...`). Resolve them against the
   * configured API root so a virtual prefix such as `/staging` is kept. Anything else is
   * dropped rather than loaded from an unexpected host.
   */
  private mediaUrl(value: unknown): string | null {
    const path = text(value);
    if (!path) return null;
    if (path.startsWith(environment.apiRoot)) return path;
    const logical = path.replace(/^\/+/, '');
    return logical.startsWith('api/whats-on/') ? this.apiUrl(logical) : null;
  }

  private apiUrl(path: string): string {
    return `${environment.apiRoot}${path}`;
  }

  /** Runs at most MAX_PARALLEL_AVAILABILITY_READS requests at once; the rest wait in order. */
  private limited<T>(request: () => Observable<T>): Observable<T> {
    return new Observable<T>(subscriber => {
      let inner: Subscription | undefined;
      let started = false;
      let released = false;
      const release = () => {
        if (released) return;
        released = true;
        this.running--;
        this.waiting.shift()?.();
      };
      const start = () => {
        started = true;
        this.running++;
        inner = request().subscribe({
          next: value => subscriber.next(value),
          error: error => { release(); subscriber.error(error); },
          complete: () => { release(); subscriber.complete(); }
        });
      };
      if (this.running < MAX_PARALLEL_AVAILABILITY_READS) start();
      else this.waiting.push(start);
      return () => {
        if (!started) {
          const index = this.waiting.indexOf(start);
          if (index >= 0) this.waiting.splice(index, 1);
          return;
        }
        inner?.unsubscribe();
        release();
      };
    });
  }
}

function isBranchId(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** API JSON is camelCase like the rest of ERP; PascalCase is accepted in case the host formatter differs. */
function field<T = unknown>(source: unknown, name: string): T | undefined {
  if (!source || typeof source !== 'object') return undefined;
  const record = source as Record<string, unknown>;
  if (name in record) return record[name] as T;
  return record[name.charAt(0).toUpperCase() + name.slice(1)] as T;
}
